import { useEffect, useState } from 'react';

interface FloatingCode {
  id: number;
  x: number;
  y: number;
  text: string;
  opacity: number;
  speed: number;
  delay: number;
}

interface AgentLine {
  id: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  progress: number;
  color: string;
}

const codeSnippets = [
  'async function getData() {',
  'const result = await fetch(url);',
  'return response.json();',
  'export default App;',
  'import { useState } from "react";',
  'const [data, setData] = useState([]);',
  'useEffect(() => {',
  '  fetchData();',
  '}, []);',
  'function handleSubmit(e) {',
  '  e.preventDefault();',
  '}',
  'const user = { name, email };',
  'await db.save(user);',
  'console.log("Success!");',
  'try { await process(); }',
  'catch (err) { log(err); }',
  'export interface User {',
  '  id: string;',
  '  name: string;',
  '}',
];

export function AgentBackground() {
  const [floatingCodes, setFloatingCodes] = useState<FloatingCode[]>([]);
  const [agentLines, setAgentLines] = useState<AgentLine[]>([]);
  const [nodes, setNodes] = useState<{ x: number; y: number; pulse: boolean }[]>([]);

  // Initialize floating code snippets
  useEffect(() => {
    const codes: FloatingCode[] = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      text: codeSnippets[Math.floor(Math.random() * codeSnippets.length)],
      opacity: 0.03 + Math.random() * 0.07,
      speed: 0.5 + Math.random() * 1,
      delay: Math.random() * 5,
    }));
    setFloatingCodes(codes);

    // Create nodes (agent points)
    const nodePositions = [
      { x: 15, y: 20 },
      { x: 85, y: 25 },
      { x: 50, y: 15 },
      { x: 20, y: 75 },
      { x: 80, y: 80 },
      { x: 50, y: 85 },
      { x: 10, y: 50 },
      { x: 90, y: 50 },
    ].map(n => ({ ...n, pulse: false }));
    setNodes(nodePositions);
  }, []);

  // Animate floating codes
  useEffect(() => {
    const interval = setInterval(() => {
      setFloatingCodes(prev =>
        prev.map(code => ({
          ...code,
          y: code.y <= -10 ? 110 : code.y - code.speed * 0.1,
          x: code.x + Math.sin(Date.now() / 1000 + code.id) * 0.02,
        }))
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Animate agent connection lines
  useEffect(() => {
    const createLine = () => {
      if (nodes.length < 2) return;

      const fromNode = nodes[Math.floor(Math.random() * nodes.length)];
      const toNode = nodes[Math.floor(Math.random() * nodes.length)];

      if (fromNode === toNode) return;

      const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#22c55e', '#3b82f6'];
      const newLine: AgentLine = {
        id: Date.now(),
        fromX: fromNode.x,
        fromY: fromNode.y,
        toX: toNode.x,
        toY: toNode.y,
        progress: 0,
        color: colors[Math.floor(Math.random() * colors.length)],
      };

      setAgentLines(prev => [...prev.slice(-5), newLine]);

      // Pulse the destination node
      setNodes(prev =>
        prev.map(n =>
          n.x === toNode.x && n.y === toNode.y ? { ...n, pulse: true } : n
        )
      );
      setTimeout(() => {
        setNodes(prev =>
          prev.map(n =>
            n.x === toNode.x && n.y === toNode.y ? { ...n, pulse: false } : n
          )
        );
      }, 500);
    };

    const interval = setInterval(createLine, 1500);
    return () => clearInterval(interval);
  }, [nodes]);

  // Animate line progress
  useEffect(() => {
    const interval = setInterval(() => {
      setAgentLines(prev =>
        prev
          .map(line => ({ ...line, progress: line.progress + 0.03 }))
          .filter(line => line.progress <= 1.5)
      );
    }, 30);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-bg-primary via-bg-primary/95 to-bg-primary" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(to right, currentColor 1px, transparent 1px),
            linear-gradient(to bottom, currentColor 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating code snippets */}
      {floatingCodes.map(code => (
        <div
          key={code.id}
          className="absolute font-mono text-xs text-text-primary whitespace-nowrap transition-opacity duration-1000"
          style={{
            left: `${code.x}%`,
            top: `${code.y}%`,
            opacity: code.opacity,
            transform: 'translateX(-50%)',
          }}
        >
          {code.text}
        </div>
      ))}

      {/* SVG for lines and nodes */}
      <svg className="absolute inset-0 w-full h-full">
        {/* Connection lines */}
        {agentLines.map(line => {
          const length = Math.sqrt(
            Math.pow(line.toX - line.fromX, 2) + Math.pow(line.toY - line.fromY, 2)
          );
          const progress = Math.min(line.progress, 1);
          const currentX = line.fromX + (line.toX - line.fromX) * progress;
          const currentY = line.fromY + (line.toY - line.fromY) * progress;

          return (
            <g key={line.id}>
              {/* Trail */}
              <line
                x1={`${line.fromX}%`}
                y1={`${line.fromY}%`}
                x2={`${currentX}%`}
                y2={`${currentY}%`}
                stroke={line.color}
                strokeWidth="1"
                opacity={0.3 * (1 - line.progress * 0.5)}
              />
              {/* Moving dot */}
              {line.progress <= 1 && (
                <circle
                  cx={`${currentX}%`}
                  cy={`${currentY}%`}
                  r="3"
                  fill={line.color}
                  opacity={0.8}
                >
                  <animate
                    attributeName="r"
                    values="2;4;2"
                    dur="0.5s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
            </g>
          );
        })}

        {/* Agent nodes */}
        {nodes.map((node, i) => (
          <g key={i}>
            {/* Outer glow */}
            <circle
              cx={`${node.x}%`}
              cy={`${node.y}%`}
              r={node.pulse ? 15 : 8}
              fill="none"
              stroke="#6366f1"
              strokeWidth="1"
              opacity={node.pulse ? 0.5 : 0.2}
              className="transition-all duration-300"
            />
            {/* Inner dot */}
            <circle
              cx={`${node.x}%`}
              cy={`${node.y}%`}
              r="4"
              fill="#6366f1"
              opacity={node.pulse ? 0.8 : 0.4}
              className="transition-all duration-300"
            />
            {/* Pulse animation */}
            {node.pulse && (
              <circle
                cx={`${node.x}%`}
                cy={`${node.y}%`}
                r="4"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2"
                opacity="0.6"
              >
                <animate
                  attributeName="r"
                  from="4"
                  to="20"
                  dur="0.5s"
                  fill="freeze"
                />
                <animate
                  attributeName="opacity"
                  from="0.6"
                  to="0"
                  dur="0.5s"
                  fill="freeze"
                />
              </circle>
            )}
          </g>
        ))}
      </svg>

      {/* Radial gradient center glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)',
        }}
      />
    </div>
  );
}
